import { GitClientService } from "VersionControl/Scripts/GitClientService";
import { RepositoryContext, RepositoryType } from "VersionControl/Scripts/RepositoryContext";
import { CodeEditor } from "./TFS.Extensions.CodeEditor";

import TFS_OM_Common = require("Presentation/Scripts/TFS/TFS.OM.Common");
import Context = require("Presentation/Scripts/TFS/TFS.Host.TfsContext");

/**
 * This interface contains additional information in the uri provided by the extension so that it is processed correctly by monaco
 * For example, the uri object should contain a property "isSameRepo" which is used to determine whether to display the content in the peek window
 * preUri should be of type MonacoUri (see TFS.Extensions.CodeEditor.ExternalNavigation.ts)
 */
interface IPreprocessedLocation extends monaco.languages.Location {
    preUri: any;
}

interface ICodeLensProvider {
    provideCodeLenses(model: ISimplifiedMonacoModel, token: monaco.CancellationToken): monaco.languages.ProviderResult<monaco.languages.CodeLensList>,
}

interface IDeclarationProvider {
    provideDeclaration(model: ISimplifiedMonacoModel, position: monaco.Position, token: monaco.CancellationToken): monaco.languages.ProviderResult<IPreprocessedLocation | IPreprocessedLocation[]>,
}

interface IDefinitionProvider {
    provideDefinition: (model: ISimplifiedMonacoModel, position: monaco.Position, token: monaco.CancellationToken) => [];
}

interface IDocumentSemanticTokensProvider {
    getLightThemeTokenTextStyles(): Promise<monaco.editor.ITokenThemeRule[]>,

    getDarkThemeTokenTextStyles(): Promise<monaco.editor.ITokenThemeRule[]>,

    getLegend(): Promise<monaco.languages.SemanticTokensLegend>,

    provideDocumentSemanticTokens(model: ISimplifiedMonacoModel, token: monaco.CancellationToken): monaco.languages.ProviderResult<monaco.languages.SemanticTokens>
}

interface IDocumentSymbolProvider {
    provideDocumentSymbols(model: ISimplifiedMonacoModel, token: monaco.CancellationToken): monaco.languages.ProviderResult<monaco.languages.DocumentSymbol[]>,
}

interface IFoldingRangeProvider {
    provideFoldingRanges(model: ISimplifiedMonacoModel, context: monaco.languages.FoldingContext, token: monaco.CancellationToken): monaco.languages.ProviderResult<monaco.languages.FoldingRange[]>,
}

interface IHoverProvider {
    provideHover(model: ISimplifiedMonacoModel, position: monaco.Position, token: monaco.CancellationToken): monaco.languages.ProviderResult<monaco.languages.Hover>,
}

interface IImplementationProvider {
    provideImplementation(model: ISimplifiedMonacoModel, position: monaco.Position, token: monaco.CancellationToken): monaco.languages.ProviderResult<IPreprocessedLocation | IPreprocessedLocation[]>,
}

interface IReferenceProvider {
    provideReferences(model: ISimplifiedMonacoModel, position: monaco.Position, context: monaco.languages.ReferenceContext, token: monaco.CancellationToken): monaco.languages.ProviderResult<[]>,
}

interface ITypeDefinitionProvider {
    provideTypeDefinition(model: ISimplifiedMonacoModel, position: monaco.Position, token: monaco.CancellationToken): monaco.languages.ProviderResult<IPreprocessedLocation | IPreprocessedLocation[]>,
}

export interface ILanguageProvider {
    codeLensProvider?: ICodeLensProvider,
    declarationProvider?: IDeclarationProvider,
    definitionProvider?: IDefinitionProvider,
    documentSemanticTokensProvider?: IDocumentSemanticTokensProvider
    documentSymbolProvider?: IDocumentSymbolProvider,
    foldingRangeProvider?: IFoldingRangeProvider,
    hoverProvider?: IHoverProvider,
    implementationProvider?: IImplementationProvider,
    referenceProvider?: IReferenceProvider,
    typeDefinitionProvider?: ITypeDefinitionProvider,
}

/**
 * Additional properties needed in the repository context to properly retrieve file content for the peek window
 */
class RepositoryContextExtended extends RepositoryContext {
    private _gitRepository: {
        defaultBranch?: string,
        id: string,
        name?: string,
        project?: {
            id: string,
            name: string,
        }
    };

    constructor(tfsContext: Context.TfsContext, repositoryType: RepositoryType, rootPath: string, repoId: string) {
        super(tfsContext, repositoryType, rootPath);
        this._gitRepository = {
            id: repoId,
        };
    }

    override getRepositoryId(): string {
        return this._gitRepository.id;
    }
}

/**
 * This interface differs from the corresponding File view interface with the assumption
 * that search files always contain the commit id in the uri, so version/versionType are not explicitly passed
 */
interface ISimplifiedMonacoModel {
    uri: monaco.Uri;
}

class MonacoUri {
    constructor(
        public authority: string,
        public fragment: string,
        public path: string,
        public query: string,
        public scheme: string,
        public isSameRepo?: boolean,
        public fsPath?: string,
        public version?: string,
        public versionType?: number,
        public languageId?: string) { }

    toString(): string {
        let uriString = `${this.scheme}://${this.authority}`;
        if (this.scheme == "inmemory") {
            uriString += `${this.path}`;
        }
        if (this.query != "") {
            uriString += `?${this.query}`;
            if (this.scheme != "inmemory") {
                uriString += `&path=${this.path}`;
            }
        }
        if (this.fragment != "") {
            uriString += `#${this.fragment}`;
        }
        return uriString;
    }

    static isValid(obj: { [key: string]: any }): boolean {
        if (obj && obj["isMonacoUri"]) {
            return true;
        }
        return false;
    }

    /**
     * A method that will parse the incoming Uri as a MonacoUri, particularly in order to attach a custom toString method
     * @param obj An object containing the uri components to construct a monaco Uri
     */
    static parseAsPreUri(obj: { [key: string]: any }): MonacoUri {
        const { authority, fragment, path, query, scheme, isSameRepo, fsPath, version, versionType, languageId } = obj;
        return new MonacoUri(authority, fragment, path, query, scheme, isSameRepo, fsPath, version, versionType, languageId);
    }

    static parse(obj: MonacoUri): monaco.Uri {
        return Object.assign(new monaco.Uri(), { authority: obj.authority, scheme: obj.scheme, path: obj.path, query: obj.query, fragment: obj.fragment, toString: obj.toString });
    }
}

export class MonacoProviderProxy
    implements monaco.languages.DefinitionProvider,
    monaco.languages.ReferenceProvider,
    monaco.languages.CodeLensProvider,
    monaco.languages.DeclarationProvider,
    monaco.languages.DefinitionProvider,
    monaco.languages.DocumentSemanticTokensProvider,
    monaco.languages.DocumentSymbolProvider,
    monaco.languages.FoldingRangeProvider,
    monaco.languages.HoverProvider,
    monaco.languages.ImplementationProvider,
    monaco.languages.ReferenceProvider,
    monaco.languages.TypeDefinitionProvider {
    private gitClient: GitClientService;
    private languageProvider?: ILanguageProvider;
    private repositoryContext: RepositoryContextExtended;
    private themeName: string;

    private static languageByExt: { [key: string]: string } = {
        "cpp": "cpp",
        "cs": "csharp",
        "vb": "csharp",
        "h": "cpp",
        "java": "java",
        "js": "javascript",
        "jsx": "javascript",
        "rs": "rust",
        "ts": "typescript",
        "tsx": "typescript",
    };
    private legend?: monaco.languages.SemanticTokensLegend;

    constructor(contribution: ILanguageProvider, repoId: string, themeName: string) {
        this.languageProvider = contribution;
        this.repositoryContext = new RepositoryContextExtended(Context.TfsContext.getDefault(), RepositoryType.Git, "/", repoId);
        this.themeName = themeName;
        this.gitClient = <GitClientService>(TFS_OM_Common.ProjectCollection.getDefaultConnection().getService<GitClientService>(GitClientService));
    }

    static isKnownLanguage(knownLanguages: string[], extension: string): boolean {
        return knownLanguages.indexOf(MonacoProviderProxy.languageByExt[extension]) != -1;
    }

    registerProviders(fileExtension: string, capabilities: { [key: string]: boolean }, codeLensContext: monaco.languages.CodeLensList, disposables?: monaco.IDisposable[]) {
        const languageId = MonacoProviderProxy.languageByExt[fileExtension];
        if (codeLensContext) {
            this.languageProvider!.codeLensProvider = {
                provideCodeLenses: function (_model: monaco.editor.ITextModel, _token: monaco.CancellationToken) {
                    return codeLensContext;
                }
            }
        }
        for (let providerType in this.languageProvider) {
            if (capabilities[providerType]) {
                let provider;
                switch (providerType) {
                    case "declarationProvider":
                        provider = monaco.languages.registerDeclarationProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "definitionProvider":
                        provider = monaco.languages.registerDefinitionProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "documentSemanticTokensProvider":
                        this.initializeSemanticTokensProvider(languageId, disposables);
                        break;
                    case "documentSymbolProvider":
                        provider = monaco.languages.registerDocumentSymbolProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "foldingRangeProvider":
                        provider = monaco.languages.registerFoldingRangeProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "hoverProvider":
                        provider = monaco.languages.registerHoverProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "implementationProvider":
                        provider = monaco.languages.registerImplementationProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "referenceProvider":
                        provider = monaco.languages.registerReferenceProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "typeDefinitionProvider":
                        provider = monaco.languages.registerTypeDefinitionProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "codeLensProvider":
                        provider = monaco.languages.registerCodeLensProvider(languageId, this.languageProvider.codeLensProvider!);
                        disposables?.push(provider);
                        break;
                    default:
                        console.log(`unexpected provider type: ${providerType}`);
                        break;
                }
                console.log(`${providerType} registered`);
            }
        }
    }

    async addDiscoverability(identifiersPromise: Promise<monaco.IRange[]>, editor: CodeEditor, monacoEditor: monaco.editor.ICodeEditor) {
        identifiersPromise.then(locations => {
            editor.addHighlightingRangeOnHover(locations, monacoEditor);
        });
    }

    async removeHighlighing(editor: CodeEditor, monacoEditor: monaco.editor.ICodeEditor) {
        editor.addHighlightingRangeOnHover([], monacoEditor)
    }

    private async initializeSemanticTokensProvider(languageId: string, disposables: monaco.IDisposable[] | undefined): Promise<void> {
        // getLegend in Monaco API is not async, so do our async call first, and save in a field.
        this.legend = await this.languageProvider?.documentSemanticTokensProvider?.getLegend();

        // Set a custom theme with additional extension-provided colors for semantic tokens.
        if (!await this.trySetSemanticTokensAwareTheme()) {
            return;
        }

        // Finally, enable semantic colorization.
        const provider = monaco.languages.registerDocumentSemanticTokensProvider(languageId, this);
        disposables?.push(provider);
    }

    private async trySetSemanticTokensAwareTheme(): Promise<boolean> {

        // Figure out whether the current Azure DevOps theme is a dark or light mode theme.
        const isDarkMode = this.themeName.includes('dark');

        // Request dark or light semantic tokens rules from the extension.
        const additionalRules: monaco.editor.ITokenThemeRule[] | undefined = isDarkMode ?
            await this.languageProvider?.documentSemanticTokensProvider?.getDarkThemeTokenTextStyles() :
            await this.languageProvider?.documentSemanticTokensProvider?.getLightThemeTokenTextStyles();

        if (!additionalRules) {
            return false;
        }

        const baseThemeName: monaco.editor.BuiltinTheme = isDarkMode ? 'vs-dark' : 'vs';

        // Create a custom theme for light or dark mode that adds extension-provided colors
        // for the semantic tokens.
        const theme: monaco.editor.IStandaloneThemeData = {
            base: baseThemeName,
            inherit: true,
            colors: {},
            rules: additionalRules
        }

        const themeName = 'vs-semantic-aware-' + (isDarkMode ? 'dark' : 'light');

        monaco.editor.defineTheme(themeName, theme);
        monaco.editor.setTheme(themeName);

        return true;
    }

    getLegend(): monaco.languages.SemanticTokensLegend {
        // Monaco's getLegend() method does not have an async signature so we have
        // to simulate it by doing all of the async work to fetch the legend ahead
        // of time and store it in a field.
        return this.legend as monaco.languages.SemanticTokensLegend;
    }

    async provideDocumentSemanticTokens(model: monaco.editor.ITextModel, _lastResultId: string, token: monaco.CancellationToken): Promise<monaco.languages.SemanticTokens | monaco.languages.SemanticTokensEdits> {
        const simplifiedModel = this.simplifyMonacoTextModel(model);
        const results = await this.languageProvider?.documentSemanticTokensProvider?.provideDocumentSemanticTokens(simplifiedModel, token);
        return results as monaco.languages.SemanticTokens;
    }

    releaseDocumentSemanticTokens(resultId: string): void {
    }

    async provideCodeLenses(model: monaco.editor.ITextModel, token: monaco.CancellationToken): Promise<monaco.languages.CodeLensList> {
        let simplifiedModel = this.simplifyMonacoTextModel(model);
        let results = await this.languageProvider?.codeLensProvider?.provideCodeLenses(simplifiedModel, token);
        return results as monaco.languages.CodeLensList;
    }

    async provideDeclaration(model: monaco.editor.ITextModel, position: monaco.Position, token: monaco.CancellationToken): Promise<monaco.languages.Definition> {
        let simplifiedModel = this.simplifyMonacoTextModel(model);
        let results = await this.languageProvider?.declarationProvider?.provideDeclaration(simplifiedModel, position, token);
        this.convertUris(results);
        await this.postProcessUris(results);
        return results as monaco.languages.Definition;
    }

    async provideDefinition(model: monaco.editor.ITextModel, position: monaco.Position, token: monaco.CancellationToken): Promise<monaco.languages.Definition> {
        let simplifiedModel = this.simplifyMonacoTextModel(model);
        let results = await this.languageProvider?.definitionProvider?.provideDefinition(simplifiedModel, position, token);
        this.convertUris(results);
        await this.postProcessUris(results);
        return results as monaco.languages.Definition;
    }

    async provideDocumentSymbols(model: monaco.editor.ITextModel, token: monaco.CancellationToken): Promise<monaco.languages.DocumentSymbol[]> {
        let simplifiedModel = this.simplifyMonacoTextModel(model);
        let results = await this.languageProvider?.documentSymbolProvider?.provideDocumentSymbols(simplifiedModel, token);
        return results as monaco.languages.DocumentSymbol[];
    }

    async provideFoldingRanges(model: monaco.editor.ITextModel, context: monaco.languages.FoldingContext, token: monaco.CancellationToken): Promise<monaco.languages.FoldingRange[]> {
        let simplifiedModel = this.simplifyMonacoTextModel(model);
        let results = await this.languageProvider?.foldingRangeProvider?.provideFoldingRanges(simplifiedModel, context, token);
        return results as monaco.languages.FoldingRange[];
    }

    async provideHover(model: monaco.editor.ITextModel, position: monaco.Position, token: monaco.CancellationToken): Promise<monaco.languages.Hover> {
        let simplifiedModel = this.simplifyMonacoTextModel(model);
        let results = await this.languageProvider?.hoverProvider?.provideHover(simplifiedModel, position, token);
        return results as monaco.languages.Hover;
    }

    async provideImplementation(model: monaco.editor.ITextModel, position: monaco.Position, token: monaco.CancellationToken): Promise<monaco.languages.Definition> {
        let simplifiedModel = this.simplifyMonacoTextModel(model);
        let results = await this.languageProvider?.implementationProvider?.provideImplementation(simplifiedModel, position, token);
        await this.postProcessUris(results);
        return results as monaco.languages.Definition;
    }

    async provideReferences(model: monaco.editor.ITextModel, position: monaco.Position, context: monaco.languages.ReferenceContext, token: monaco.CancellationToken): Promise<monaco.languages.Location[]> {
        let simplifiedModel = this.simplifyMonacoTextModel(model);
        let results = await this.languageProvider?.referenceProvider?.provideReferences(simplifiedModel, position, context, token);
        this.convertUris(results);
        await this.postProcessUris(results);
        return results as monaco.languages.Location[];
    }

    async provideTypeDefinition(model: monaco.editor.ITextModel, position: monaco.Position, token: monaco.CancellationToken): Promise<monaco.languages.Definition> {
        let simplifiedModel = this.simplifyMonacoTextModel(model);
        let results = await this.languageProvider?.typeDefinitionProvider?.provideTypeDefinition(simplifiedModel, position, token);
        this.convertUris(results);
        await this.postProcessUris(results);
        return results as monaco.languages.Definition;
    }

    // Because monaco's TextModel object is too large to send via rpc, this creates an object with only the relevant properties to send over rpc
    private simplifyMonacoTextModel(fullTextModel: { [key: string]: any }): ISimplifiedMonacoModel {
        return {
            uri: fullTextModel["uri"],
        };
    }

    private addModelIfNotExists(content: string, language: string, uri: monaco.Uri): void {
        let model = monaco.editor.getModel(uri);
        if (!model) {
            monaco.editor.createModel(content, language, uri);
        }
        else if (model.getLineContent(1) === "Loading...") {
            model.setValue(content);
        }
    }

    private convertUris(results: IPreprocessedLocation[] | IPreprocessedLocation | null | undefined): void {
        if (results instanceof Array) {
            for (let r of results) {
                r.preUri = MonacoUri.parseAsPreUri(r.preUri);
                r.uri = MonacoUri.parse(r.preUri);
            }
        }
        else if (results) {
            results.preUri = MonacoUri.parseAsPreUri(results.preUri);
            results.uri = MonacoUri.parse(results.preUri);
        }
    }

    private async postProcessUris(results: IPreprocessedLocation[] | IPreprocessedLocation | null | undefined): Promise<void> {
        if (results instanceof Array) {
            for (let r of results) {
                await this.postProcessUri(r);
            }
        } else if (results) {
            await this.postProcessUri(results);
        }
    }

    private async postProcessUri(result: IPreprocessedLocation): Promise<void> {
        if (result.preUri.isSameRepo) {
            let model = monaco.editor.getModel(result.uri);
            if (!model) {
                this.addModelIfNotExists("Loading...", result.preUri.languageId!, result.uri);
                const versionWithPrefix = `G${result.preUri.versionType === 0 ? "B" : "C"}${result.preUri.version}`;
                this.gitClient.beginGetItemContentWithRepoId(this.repositoryContext, this.repositoryContext.getRepositoryId(), result.uri.path, versionWithPrefix, (content) => {
                    this.addModelIfNotExists(content, result.preUri.languageId!, result.uri);
                }, (error) => { console.log(`Error retrieving content: ${error}`) });
            }
        }
        else {
            this.addModelIfNotExists("Cross-repository preview not yet supported. Stay tuned!", result.preUri.languageId!, result.uri);
        }
    }
}
